import React, { useState, Fragment } from "react";
import {
  Modal,
  message,
  Button,
  Divider,
  Tag,
  Icon,
  notification,
  Spin,
} from "antd";
import * as texts from "./locales";
import api from "../../../../services/api";
import { formatters } from "../../../../utils/formatters";
import "./index.css";
import { useOnUpdate } from "../../../../hooks";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { isArray, isBoolean } from "lodash";
import { disabledIdleFee } from "..";
import { disabled_activation_fee } from "../../../../components/ActivationFee";
import {
  ACTIVATE_ACTIVATION_FEE,
  ACTIVATION_FEE_VALUE,
  NO_ACTIVATION_FEE,
  CURRENT_ACTIVATION_FEE,
  ACTIVATION_FEE_CHARGE_VALUE_EXEMPTION,
  ACTIVATION_FEE_KWH_VALUE_EXEMPTION,
} from "../../../../locales";

const ModalBatchOperation = ({
  isOpen,
  closeModal,
  freire,
  stations: stationsProps,
  paymentCharge,
  country,
  acceptCoupon,
  posPayment,
  idleFee,
  activation_fee,
  price_per_hour_enable,
  price_per_hour,
}) => {
  const history = useHistory();

  const DEFAULT_LOADING_STATIONS = false;
  const DEFAULT_STATIONS = [];
  const DEFAULT_EXPANDED = false;
  const DEFAULT_LOADING_SAVE = false;
  const DEFAULT_PRICE_PER_HOUR_EXPANDED = false;

  const [loadingStations, setLoadingStations] = useState(
    DEFAULT_LOADING_STATIONS
  );
  const [stations, setStations] = useState(DEFAULT_STATIONS);
  const [expanded, setExpanded] = useState(DEFAULT_EXPANDED);
  const [loadingSave, setLoadingSave] = useState(DEFAULT_LOADING_SAVE);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pricePerHourExpanded, setPricePerHourExpanded] = useState(
    DEFAULT_PRICE_PER_HOUR_EXPANDED
  );

  const resetState = () => {
    setLoadingStations(DEFAULT_LOADING_STATIONS);
    setStations(DEFAULT_STATIONS);
    setExpanded(DEFAULT_EXPANDED);
    setLoadingSave(DEFAULT_LOADING_SAVE);
  };

  async function getStations() {
    try {
      setLoadingStations(true);
      setStations([]);

      const { data } = await api.post(`connected_stations`, {
        page: 1,
        limit: 1000,
        station_ids: stationsProps.map((station) => station.stationID),
      });

      setLoadingStations(false);

      let filteredStations = [];

      if (idleFee.enabled || activation_fee.enabled) {
        filteredStations = data.docs.filter((station) => {
          const hasPaymentEnabled = station.paymentCharge?.enabled;

          const hasValidPaymentMethod =
            !activation_fee.exemption_by_consumption_enabled ||
            station?.paymentCharge?.method !== "Fixo";

          return hasPaymentEnabled && hasValidPaymentMethod;
        });
      }

      setStations(filteredStations.length ? filteredStations : data.docs);
    } catch (error) {
      message.error(freire(texts.ERROR_GET_STATIONS));
      closeModal();
    }
  }

  useOnUpdate(() => {
    if (isOpen) {
      getStations();
    } else {
      resetState();
    }
  }, [isOpen]);

  const onUpdateBatchOperation = (userConfirmed) => async () => {
    try {
      const _paymentCharge = { ...paymentCharge };

      if (_paymentCharge.enabled && _paymentCharge.method === "") {
        message.warning(freire(texts.ERROR_INFO_CHARGE_PAYMENT_METHOD));
        return;
      }

      _paymentCharge.value = parseInt(
        _paymentCharge?.value?.replace(/[.,]/g, "") ?? 0
      );
      if (_paymentCharge.enabled && _paymentCharge.value === 0) {
        message.warning(freire(texts.ERROR_INFO_CHARGE_PAYMENT));
        return;
      }

      if (
        !userConfirmed &&
        (_paymentCharge.value === "" || _paymentCharge.value === 0)
      ) {
        setShowWarningModal(true);
        return;
      }

      const fieldsToBeUpdated = {
        country,
        stationIds: stations.map((station) => station._id),
      };

      if (isBoolean(idleFee.enabled)) {
        fieldsToBeUpdated.idleFee = {
          ...idleFee,
          value: parseInt(idleFee?.value?.replace(",", "") ?? 0),
          gracePeriod: idleFee.gracePeriod
            ? moment
                .duration(Number(idleFee.gracePeriod), "minutes")
                .toISOString()
            : "",
        };
      }

      if (isBoolean(activation_fee.enabled)) {
        fieldsToBeUpdated.activation_fee = {
          ...activation_fee,
          value: parseInt(activation_fee?.value?.replace(",", "") ?? 0),
          exemption_lower_charge_value: parseInt(
            activation_fee?.exemption_lower_charge_value?.replace(",", "") ?? 0
          ),
        };
      }

      if (isBoolean(price_per_hour_enable)) {
        fieldsToBeUpdated.price_per_hour_enable = price_per_hour_enable;

        if (price_per_hour_enable) {
          if (isArray(price_per_hour)) {
            fieldsToBeUpdated.price_per_hour = price_per_hour.map((hour) => ({
              ...hour,
              value: parseInt(hour?.value?.replace(/[.,]/g, "") ?? 0),
              start_time: hour.start_time.format("HH:mm"),
              end_time: hour.end_time.format("HH:mm"),
              timezone: moment.tz.guess(),
            }));
          }
        }
      }

      if (isBoolean(_paymentCharge.enabled)) {
        if (!_paymentCharge.enabled) {
          fieldsToBeUpdated.idleFee = disabledIdleFee.idleFee;
          fieldsToBeUpdated.activation_fee =
            disabled_activation_fee.activation_fee;
        }
        fieldsToBeUpdated.paymentCharge = _paymentCharge;
      }

      if (isBoolean(acceptCoupon)) {
        fieldsToBeUpdated.acceptCoupon = acceptCoupon;
      }

      if (isBoolean(posPayment)) {
        fieldsToBeUpdated.posPayment = posPayment;
      }

      setLoadingSave(true);

      await api.put(`/batch-update/stations`, fieldsToBeUpdated);

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.STATION_EDITED),
      });

      setTimeout(() => {
        // setLoadingSave(false);
        history.push("/dashboard/connected_stations");
      }, 1000);
    } catch (error) {
      const message = error?.response?.data?.message ?? error.message;

      console.error(message);
      console.error(error);

      setLoadingSave(false);

      notification.error({
        message: freire(texts.WENT_WRONG),
        description: message,
      });
    }
  };

  const toggleExpansion = () => {
    setExpanded(!expanded);
  };

  const togglePricePerHourExpansion = () => {
    setPricePerHourExpanded(!pricePerHourExpanded);
  };

  const renderPricePerHour = () => {
    return price_per_hour?.map((hour) => {
      const start_time = hour.start_time.format("HH:mm");
      const end_time = hour.end_time.format("HH:mm");
      return (
        <Fragment
          style={{ fontSize: 10 }}
          key={start_time + end_time + hour.value}
        >
          <Divider />
          <div>
            <span>{freire(texts.VALUE)}</span>
            <Tag style={{ marginLeft: 4, fontSize: 10 }} color="green">
              {`${formatters.getCurrencySymbol(
                freire.userLanguage,
                stations[0].currency
              )} ${hour.value}`}
            </Tag>
          </div>
          <div style={{ marginTop: 6 }}>
            <span>{freire(texts.OF)}</span>
            <Tag style={{ marginLeft: 4, fontSize: 10 }} color="blue">
              {start_time}
            </Tag>
            <span>{freire(texts.TO)}</span>
            <Tag style={{ marginLeft: 4, fontSize: 10 }} color="blue">
              {end_time}
            </Tag>
          </div>
        </Fragment>
      );
    });
  };

  const renderStations = () => {
    const stationsToRender = expanded ? stations : stations.slice(0, 10);
    return stationsToRender.map((station) => (
      <tr className="stations-modal-batch" key={station.stationID}>
        <td className="id-column">{station.stationID}</td>
        <td className="name-column">{station.name}</td>
        <td className="payment-column">
          {station.paymentCharge && station.paymentCharge.enabled ? (
            <Fragment>
              <Tag color="blue">{station.paymentCharge.method}</Tag>
              <Tag style={{ marginTop: 5 }} color="green">
                {formatters.currency(
                  freire.userLanguage,
                  station.currency
                )(station.paymentCharge.value / 100)}
              </Tag>
              {station.price_per_hour_enable && (
                <Tag style={{ fontSize: 10 }} color="orange">
                  {freire(texts.PRICE_PER_HOUR)}
                </Tag>
              )}
            </Fragment>
          ) : (
            freire(texts.NO_PRICE)
          )}
        </td>
        <td className="idle-fee-column">
          {station.idleFee?.enabled ? (
            <>
              <Tag style={{ marginTop: 5 }} color="blue">
                {moment.duration(station.idleFee.gracePeriod).humanize()}
              </Tag>
              <Tag style={{ marginTop: 5 }} color="green">
                {formatters.currency(
                  freire.userLanguage,
                  station.currency
                )(station.idleFee.value / 100)}
              </Tag>
            </>
          ) : (
            freire(texts.NO_IDLE_FEE)
          )}
        </td>
        <td className="activation-fee-column">
          {station.activation_fee?.enabled ? (
            <Tag style={{ marginTop: 5 }} color="green">
              {formatters.currency(
                freire.userLanguage,
                station.currency
              )(station.activation_fee.value / 100)}
            </Tag>
          ) : (
            freire(NO_ACTIVATION_FEE)
          )}
        </td>
      </tr>
    ));
  };

  const hasChangedStations =
    isBoolean(activation_fee?.enabled) ||
    isBoolean(idleFee?.enabled) ||
    isBoolean(posPayment) ||
    isBoolean(acceptCoupon) ||
    isBoolean(paymentCharge?.enabled);

  const content = loadingStations ? (
    <Spin style={{ display: "block", marginTop: 25, marginBottom: 25 }} />
  ) : stations.length === 0 ? (
    <span className="warning-modal">
      <Icon
        type="warning"
        style={{ color: "#FCD429", fontSize: 20, paddingRight: 8 }}
      />
      {freire(texts.WARNING_NO_STATIONS_TO_BE_UPDATED)}
    </span>
  ) : (
    <div className="modal-batch">
      <div className="modal-batch-content">
        <div className="modal-batch-header">
          <span>{freire(texts.STATION_SELETION)}</span>
        </div>
        <div className="modal-batch-body">
          <table>
            <thead>
              <tr className="table-head">
                <th className="id-station">{freire(texts.ID)}</th>
                <th className="name-station">{freire(texts.NAME)}</th>
                <th className="payment-station">
                  {freire(texts.CURRENT_CHARG_PAYMENT)}
                </th>
                <th className="idle-fee-station">
                  {freire(texts.CURRENT_IDLE_FEE)}
                </th>
                <th className="activation-fee-station">
                  {freire(CURRENT_ACTIVATION_FEE)}
                </th>
              </tr>
            </thead>
            <tbody>{renderStations()}</tbody>
          </table>

          {stations.length > 10 && (
            <Button type="link" onClick={toggleExpansion}>
              {expanded ? "Mostrar menos ▲" : "Mostrar mais ▼"}
            </Button>
          )}

          <span className="updated-price">{freire(texts.UPDATED_PRICE)}</span>

          <div className="updated-price-content">
            <div className="charge-model">
              <span
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: 16,
                }}
              >
                {freire(texts.PAYMENT_CHARGE)}
              </span>
              {paymentCharge && isBoolean(paymentCharge.enabled) ? (
                paymentCharge.enabled ? (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{freire(texts.CHARGE_MODEL)}</span>
                      <span>
                        <Fragment>
                          <Tag color="blue">{paymentCharge.method}</Tag>
                        </Fragment>
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{freire(texts.VALUE)}</span>
                      <span>
                        <Fragment>
                          <Tag color="green">
                            {formatters.getCurrencySymbol(
                              freire.userLanguage,
                              stations[0].currency
                            )}{" "}
                            {paymentCharge.value}
                          </Tag>
                        </Fragment>
                      </span>
                    </div>
                    {price_per_hour_enable && (
                      <>
                        <Divider />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ fontSize: 12 }}>
                            {freire(texts.HAS_CUSTOM_PRICE_PER_HOUR)}
                          </span>
                          {pricePerHourExpanded && (
                            <div>{renderPricePerHour()}</div>
                          )}
                          <Button
                            type="link"
                            onClick={togglePricePerHourExpansion}
                            icon={pricePerHourExpanded ? "up" : "down"}
                            style={{ textAlign: "left" }}
                          >
                            {freire(texts.SHOW_MORE)}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <span>{freire(texts.NO_ACCEPT_PAYMENT)}</span>
                )
              ) : (
                <span>{freire(texts.NO_ALTERATIONS)}</span>
              )}
            </div>

            <div className="charge-model">
              <span
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: 16,
                }}
              >
                {freire(texts.COUPON_CHARGE)}
              </span>
              <span>
                <Fragment>
                  {isBoolean(acceptCoupon)
                    ? acceptCoupon
                      ? freire(texts.ACCEPT_COUPON)
                      : freire(texts.NO_ACCEPT_COUPON)
                    : freire(texts.NO_ALTERATIONS)}
                </Fragment>
              </span>
            </div>

            <div className="charge-model">
              <span
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: 16,
                }}
              >
                {freire(texts.IDLE_FEE)}
              </span>

              {isBoolean(idleFee.enabled) ? (
                idleFee.enabled ? (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {freire(texts.GRACE_PERIOD)}
                      <Fragment>
                        <Tag style={{ marginBottom: 8 }} color="blue">
                          {idleFee.gracePeriod} min
                        </Tag>
                      </Fragment>
                    </span>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {freire(texts.VALUE_PER_MIN)}
                      <Fragment>
                        <Tag color="green">
                          {formatters.getCurrencySymbol(
                            freire.userLanguage,
                            stations[0].currency
                          )}{" "}
                          {idleFee.value}
                        </Tag>
                      </Fragment>
                    </span>
                  </div>
                ) : (
                  <span>{freire(texts.NO_ACCEPT_IDLE_FEE)}</span>
                )
              ) : (
                <span>{freire(texts.NO_ALTERATIONS)}</span>
              )}
            </div>
            <div className="charge-model">
              <span
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: 16,
                }}
              >
                {freire(ACTIVATE_ACTIVATION_FEE)}
              </span>

              {isBoolean(activation_fee.enabled) ? (
                activation_fee.enabled ? (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {freire(ACTIVATION_FEE_VALUE)}
                      <Fragment>
                        <Tag color="green">
                          {formatters.getCurrencySymbol(
                            freire.userLanguage,
                            stations[0].currency
                          )}{" "}
                          {activation_fee.value}
                        </Tag>
                      </Fragment>
                    </span>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {freire(ACTIVATION_FEE_KWH_VALUE_EXEMPTION)}
                      <Fragment>
                        <Tag style={{ marginBottom: 8 }} color="blue">
                          {activation_fee.exemption_lower_stop_value} KWh
                        </Tag>
                      </Fragment>
                    </span>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {freire(
                        ACTIVATION_FEE_CHARGE_VALUE_EXEMPTION(
                          formatters.getCurrencySymbol(
                            freire.userLanguage,
                            stations[0].currency
                          )
                        )
                      )}
                      <Fragment>
                        <Tag style={{ marginBottom: 8 }} color="blue">
                          {formatters.getCurrencySymbol(
                            freire.userLanguage,
                            stations[0].currency
                          )}
                          {activation_fee.exemption_lower_charge_value}
                        </Tag>
                      </Fragment>
                    </span>
                  </div>
                ) : (
                  <span>{freire(NO_ACTIVATION_FEE)}</span>
                )
              ) : (
                <span>{freire(texts.NO_ALTERATIONS)}</span>
              )}
            </div>
          </div>
        </div>

        <Divider style={{ margin: "30px 0 10px 0" }} />

        <span className="update-confirm">
          <Icon
            type="warning"
            style={{ color: "#FCD429", fontSize: 20, paddingTop: 4 }}
          />
          {hasChangedStations ? (
            <div>
              {freire(texts.UPDATE_CONFIRM)}
              <br />
              {freire(texts.PAYMENT_DISABLE_IDLE_FEE)}
            </div>
          ) : (
            freire(texts.NO_CHANGES)
          )}
        </span>
        <div className="modal-batch-footer">
          <Button
            disabled={loadingSave}
            onClick={closeModal}
            style={{ marginRight: "10px", width: "120px", height: "30px" }}
          >
            {freire(texts.CANCEL)}
          </Button>
          {hasChangedStations && (
            <Button
              disabled={loadingSave}
              type="primary"
              onClick={onUpdateBatchOperation(false)}
              style={{ width: "120px", height: "30px" }}
            >
              {freire(texts.UPDATE)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const warningModal = (
    <Modal
      visible={showWarningModal}
      onCancel={() => setShowWarningModal(false)}
      footer={[
        <Button key="cancel" onClick={() => setShowWarningModal(false)}>
          {freire(texts.CANCEL)}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onUpdateBatchOperation(true)}
        >
          {freire(texts.YES_UPDATE)}
        </Button>,
      ]}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <Icon type="warning" style={{ color: "#FCD429", fontSize: 40 }} />
        <span className="confirm-update-modal" style={{ marginLeft: 10 }}>
          {freire(texts.CONFIRM_UPDATE)}
        </span>
      </div>
    </Modal>
  );

  return (
    <Fragment>
      <Modal
        visible={isOpen}
        onCancel={closeModal}
        footer={null}
        width={1150}
        height={500}
      >
        {content}
      </Modal>
      {warningModal}
    </Fragment>
  );
};

export default ModalBatchOperation;
